import dayjs from 'dayjs';
import {
  Duration,
  durationToNumber,
  getFormattedSurchargeRate,
  isWholeLife,
  PersonInsurance,
  RiderType,
} from '../insurance';
import {getMonthlyPrice, Protection} from '../insurance/protection';
import {TextTranslations} from '../question/utils';
import {formatCurrency} from '../utils';
import {DATE_FORMAT} from '../utils-const';

export interface ModificationNoticeBreakdown {
  preConditionText: TextTranslations;
  postConditionText: TextTranslations;
  modificationNoticeText: TextTranslations;
  conditionText?: string;
}

export function buildModificationNoticeBreakdown(
  modificationDate: Date,
  clientFullName: string,
  policyNumber: string,
  proposalCompletionDate: Date,
  initialProtectionSummary: ProtectionSummary,
  initialProtection: Protection,
  surchargedProtectionSummary?: ProtectionSummary,
  surchargedProtection?: Protection & {surchargeRate: number},
  conditionMarkdownText?: string,
  isAdvisorModification?: boolean,
): ModificationNoticeBreakdown {
  const preConditionText: TextTranslations = {fr: '', en: ''};
  const postConditionText: TextTranslations = {fr: '', en: ''};

  const bilingualDateString = `**Date : ${dayjs(modificationDate).format(DATE_FORMAT)}**\n\n`;
  preConditionText.fr += bilingualDateString;
  preConditionText.en += bilingualDateString;

  preConditionText.fr += `**Nom de l'assuré : ${clientFullName}\nNuméro de la police : ${policyNumber}**\n\n`;
  preConditionText.en += `**Insured name: ${clientFullName}\nPolicy number: ${policyNumber}**\n\n`;

  preConditionText.fr += `**Objet: Avis de modification**\n\n`;
  preConditionText.en += `**Object: Notice of modification**\n\n`;

  preConditionText.fr += `Madame/Monsieur,\n\n`;
  preConditionText.en += `Sir/Madam,\n\n`;

  preConditionText.fr += `Nous avons procédé à l'étude de votre demande d'assurance et nous désirons vous aviser de notre décision à ce sujet. `;
  preConditionText.en += `We have reviewed your insurance application and wish to inform you of our decision on this matter. `;

  if (surchargedProtection && surchargedProtectionSummary) {
    preConditionText.fr += `Humania Assurance inc. a apporté les modifications suivantes à la proposition d’assurance et/ou à la police sous-mentionnée:\n\n`;
    preConditionText.en += `Humania Assurance inc. has made the following changes to the insurance proposal and/or policy below:\n\n`;

    const protectionSummaryText = getProtectionSummaryText(initialProtectionSummary, initialProtection);
    preConditionText.fr += `Résumé de la protection initiale :\n${protectionSummaryText.fr}\n`;
    preConditionText.en += `Summary of initial protection:\n${protectionSummaryText.en}\n`;

    const rate = getFormattedSurchargeRate(surchargedProtection.surchargeRate);

    const removedRidersText: TextTranslations = {fr: '', en: ''};
    const removedRiders = surchargedProtectionSummary.riders?.filter((r) => r.isRemoved);
    if (removedRiders?.length) {
      removedRidersText.fr += ` **sans les avenants suivants :**\n`;
      removedRidersText.en += ` **and without the following riders:**\n`;
      removedRiders.forEach((r) => {
        removedRidersText.fr += `- ${r.description.fr}\n`;
        removedRidersText.en += `- ${r.description.en}\n`;
      });
    } else {
      removedRidersText.fr += '.';
      removedRidersText.en += '.';
    }

    preConditionText.fr += `Cette police est émise à ${rate}&nbsp;%${removedRidersText.fr}\n\n`;
    preConditionText.en += `This policy is issued with a rating of ${rate}%${removedRidersText.en}\n\n`;

    const surchargedProtectionSummaryText = getProtectionSummaryText(
      surchargedProtectionSummary,
      surchargedProtection,
      initialProtection,
    );
    if (isAdvisorModification) {
      preConditionText.fr += `Résumé de la protection après la modification de l'assureur ainsi que les changements de couverture convenus avec votre conseiller en sécurité financière :\n${surchargedProtectionSummaryText.fr}\n`;
      preConditionText.en += `Summary of coverage after modification from insurer and changes in coverage agreed with your financial security advisor:\n${surchargedProtectionSummaryText.en}\n`;
    } else {
      preConditionText.fr += `Résumé de la protection après la modification de l'assureur :\n${surchargedProtectionSummaryText.fr}\n`;
      preConditionText.en += `Summary of protection after the insurer's modification:\n${surchargedProtectionSummaryText.en}\n`;
    }
  } else {
    preConditionText.fr += `Humania Assurance inc. a apporté les modifications suivantes à votre proposition d’assurance et/ou à votre police:\n\n`;
    preConditionText.en += `Humania Assurance inc. has made the following changes to your insurance proposal and/or policy:\n\n`;
  }

  postConditionText.fr += `J'ai pris connaissance de la(des) modification(s) ci-haute(s) qui a(ont) été apportée(s) par Humania Assurance inc. et j'accepte la police modifiée.\n\n`;
  postConditionText.en += `I have reviewed the above modification(s) made by Humania Assurance inc. and I accept the modified policy.\n\n`;

  postConditionText.fr += `De plus, je confirme également qu'il n'y a pas eu de changement dans mon état de santé depuis le ${dayjs(
    proposalCompletionDate.toString(),
  ).format(DATE_FORMAT)}.\n`;
  postConditionText.en += `Furthermore, I also confirm that there has been no change in my health status since ${dayjs(
    proposalCompletionDate.toString(),
  ).format(DATE_FORMAT)}.\n`;

  return {
    preConditionText,
    postConditionText,
    modificationNoticeText: {
      en: `${preConditionText.en}${conditionMarkdownText ?? ''}${postConditionText.en}`,
      fr: `${preConditionText.fr}${conditionMarkdownText ?? ''}${postConditionText.fr}`,
    },
    conditionText: conditionMarkdownText,
  };
}

export interface ProtectionSummary {
  baseInsurance: {description: TextTranslations; coverage: number; price: number};
  riders?: RiderSummary[];
}

export interface RiderSummary {
  type: RiderType | Duration;
  description: TextTranslations;
  coverage: number;
  price: number;
  isRemoved?: boolean;
}

export function getProtectionSummaryText(
  summary: ProtectionSummary,
  associatedProtection: Protection,
  replacedProtection?: Protection,
): TextTranslations {
  const {baseInsurance, riders} = summary;

  const text: TextTranslations = {fr: '', en: ''};
  text.fr += `- ${baseInsurance.description.fr} : ${formatCurrency(baseInsurance.coverage, 'fr', false)}\n`;
  text.en += `- ${baseInsurance.description.en}: ${formatCurrency(baseInsurance.coverage, 'en', false)}\n`;
  riders?.forEach((r) => {
    if (!r.isRemoved) {
      text.fr += `- ${r.description.fr} : ${formatCurrency(r.coverage, 'fr', false)}\n`;
      text.en += `- ${r.description.en}: ${formatCurrency(r.coverage, 'en', false)}\n`;
    }
  });

  text.fr += `- Prime mensuelle totale : ${formatCurrency(
    getMonthlyPrice(associatedProtection.personInsurances),
    'fr',
    true,
  )}${
    replacedProtection
      ? ` **(au lieu de ${formatCurrency(
          getMonthlyPrice(replacedProtection.personInsurances),
          'fr',
          true,
        )})**`
      : ''
  }\n`;
  text.en += `- Total monthly premium: ${formatCurrency(
    getMonthlyPrice(associatedProtection.personInsurances),
    'en',
    true,
  )}${
    replacedProtection
      ? ` **(instead of ${formatCurrency(
          getMonthlyPrice(replacedProtection.personInsurances),
          'en',
          true,
        )})**`
      : ''
  }\n`;

  return text;
}

export function getDescriptionFromDuration(duration: Duration): TextTranslations {
  if (isWholeLife(duration)) {
    return {fr: 'Assurance permanente', en: 'Whole life insurance'};
  }

  const yearsForDuration = durationToNumber(duration);
  return {
    fr: `Assurance temporaire ${yearsForDuration} ${yearsForDuration > 1 ? 'ans' : 'an'}`,
    en: `${yearsForDuration}-year term insurance`,
  };
}

export function getDescriptionFromRider(riderType: RiderType): TextTranslations {
  switch (riderType) {
    case RiderType.CHILD:
      return {fr: `Assurance vie pour enfants`, en: 'Life insurance for children'};
    case RiderType.CANCER:
      return {fr: `Assurance cancer`, en: 'Cancer insurance'};
    case RiderType.DISABILITY:
      return {fr: `Assurance invalidité`, en: 'Disability insurance'};
    default:
      throw new Error(`Unknown rider type: ${riderType}`);
  }
}

export function getPriceMinusRiders(personInsurance: PersonInsurance): number {
  let basePrice = personInsurance.price;
  personInsurance.riders?.forEach((rider) => {
    if (!rider.price) {
      throw new Error('Rider price is missing');
    }
    basePrice -= rider.price;
  });
  return basePrice;
}

export function buildProtectionSummary(protection: Protection): ProtectionSummary {
  const baseInsurance = protection.personInsurances.find((i) => i.priceBreakdown.isBaseInsurance)!;

  const riders: RiderSummary[] = protection.personInsurances
    .filter((pi) => pi.duration !== baseInsurance.duration)
    .map((pi) => ({
      description: getDescriptionFromDuration(pi.duration),
      coverage: pi.coverage,
      price: getPriceMinusRiders(pi),
      type: pi.duration,
    }));

  baseInsurance.riders?.forEach((r) => {
    if (!r.price) {
      throw new Error('Rider price is missing');
    }
    riders.push({
      description: getDescriptionFromRider(r.type),
      coverage: r.coverage,
      price: r.price,
      type: r.type,
    });
  });

  return {
    baseInsurance: {
      description: getDescriptionFromDuration(baseInsurance.duration),
      coverage: baseInsurance.coverage,
      price: getPriceMinusRiders(baseInsurance),
    },
    riders: riders.length > 0 ? riders : undefined,
  };
}
